import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

import focus from './focus.module.css';
import styles from './Row.module.css';

interface RowBase {
  /** Left-hand content: an icon, a category chip, a label. */
  readonly children: ReactNode;
  /** Right-hand content: an amount, a chevron, a toggle. */
  readonly trailing?: ReactNode;
}

export type RowProps = RowBase &
  (
    | ({ readonly onPress?: undefined } & Omit<HTMLAttributes<HTMLDivElement>, 'children'>)
    | ({ readonly onPress: () => void } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'>)
  );

/**
 * A line in a list.
 *
 * With `onPress` it renders a real `<button>` rather than a div with a click handler, which is the whole
 * point: a button is reachable by keyboard, announced as a button, and pressed by Enter and Space without
 * anyone writing key handling. A tappable div is none of those things.
 */
export function Row({ children, trailing, ...rest }: RowProps): React.JSX.Element {
  if ('onPress' in rest && rest.onPress) {
    const { onPress, className, ...buttonProps } = rest as { onPress: () => void; className?: string };

    return (
      <button
        type="button"
        onClick={onPress}
        className={[styles.row, styles.pressable, focus.focusable, className ?? ''].filter(Boolean).join(' ')}
        {...buttonProps}
      >
        <span className={styles.grow}>{children}</span>
        {trailing}
      </button>
    );
  }

  const { className, ...divProps } = rest as { className?: string };

  return (
    <div className={[styles.row, className ?? ''].filter(Boolean).join(' ')} {...divProps}>
      <span className={styles.grow}>{children}</span>
      {trailing}
    </div>
  );
}
